import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../supabaseAuth';
import { CreditCard, Plus, Trash, House } from 'react-bootstrap-icons';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import apiService, { api } from '../services/api';
import AddressModal from '../components/AddressModal';
import PaymentMethodForm from '../components/PaymentMethodForm';
import SuspensionCheck from '../components/common/SuspensionCheck';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Spinner Component
const Spinner = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
};

// Alert Component
const Alert = ({ variant = 'info', children, className = '' }) => {
  const variants = {
    danger: 'bg-red-50 text-red-800 border-red-200',
    success: 'bg-green-50 text-green-800 border-green-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200'
  };

  return (
    <div className={`border rounded-lg p-4 ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

// Badge Component
const Badge = ({ variant = 'primary', children, className = '' }) => {
  const variants = {
    primary: 'bg-blue-500 text-white',
    success: 'bg-green-500 text-white',
    danger: 'bg-red-500 text-white',
    secondary: 'bg-gray-500 text-white'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Modal Component
const Modal = ({ show, onHide, children, size = 'md', title }) => {
  if (!show) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onHide}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${sizeClasses[size]} w-full`}>
          {title && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <button
                onClick={onHide}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

// Address Card Component
const AddressCard = ({ address, onDelete, onSetDefault, isDefault }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
      <div className="flex justify-between items-start">
        <div className="flex items-start">
          <div className="mr-3 text-2xl">
            <House />
          </div>
          <div>
            <div className="font-semibold">
              {address.first_name} {address.last_name}
              {isDefault && <Badge variant="primary" className="ml-2">Default</Badge>}
            </div>
            <div className="text-gray-600 text-sm mt-1">
              {address.address_line_1}
              {address.address_line_2 && <><br />{address.address_line_2}</>}
              <br />
              {address.city}, {address.state} {address.zip_code}
              <br />
              {address.country}
              {address.phone && <><br />Phone: {address.phone}</>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!isDefault && (
            <button
              onClick={() => onSetDefault(address.id)}
              className="px-3 py-1 text-sm border border-black text-black rounded-full hover:bg-gray-100 transition-colors"
            >
              Set Default
            </button>
          )}
          <button
            onClick={() => onDelete(address.id)}
            className="px-3 py-1 text-sm border border-red-500 text-red-600 rounded-full hover:bg-red-50 transition-colors"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Payment Method Card Component
const PaymentMethodCard = ({ paymentMethod, onDelete, onSetDefault, isDefault }) => {
  const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      default:
        return '💳';
    }
  };

  // Check if card is expired
  const isExpired = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const expYear = paymentMethod.exp_year;
    const expMonth = paymentMethod.exp_month;

    if (expYear < currentYear) return true;
    if (expYear === currentYear && expMonth < currentMonth) return true;
    return false;
  };

  const expired = isExpired();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
      <div className="flex justify-between items-start">
        <div className="flex items-center">
          <div className="mr-3 text-2xl">
            {getCardIcon(paymentMethod.brand)}
          </div>
          <div>
            <div className="font-semibold">
              {paymentMethod.brand?.toUpperCase()} •••• {paymentMethod.last4}
              {isDefault && <Badge variant="primary" className="ml-2">Default</Badge>}
              {expired && <Badge variant="danger" className="ml-2">Expired</Badge>}
            </div>
            <div className="text-gray-600 text-sm">
              Expires {paymentMethod.exp_month?.toString().padStart(2, '0')}/{paymentMethod.exp_year}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!isDefault && (
            <button
              onClick={() => onSetDefault(paymentMethod.id)}
              className="px-3 py-1 text-sm border border-black text-black rounded-full hover:bg-gray-100 transition-colors"
            >
              Set Default
            </button>
          )}
          <button
            onClick={() => onDelete(paymentMethod.id)}
            className="px-3 py-1 text-sm border border-red-500 text-red-600 rounded-full hover:bg-red-50 transition-colors"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Payment Method Form Component
const AddPaymentMethodForm = ({ onSuccess, onCancel }) => {
  const handlePaymentMethodSuccess = (paymentMethodId) => {
    onSuccess();
  };

  return (
    <PaymentMethodForm
      onSuccess={handlePaymentMethodSuccess}
      onCancel={onCancel}
      buttonText="Add Payment Method"
      autoFocus={true}
    />
  );
};

const Account = () => {
  const navigate = useNavigate();
  const { user, logout, supabase } = useSupabase();
  const [showEditForm, setShowEditForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState('');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalLoading, setAddressModalLoading] = useState(false);

  // Email preferences state
  const [emailPreferences, setEmailPreferences] = useState({
    email_notifications: true,
    price_alerts: true,
    delivery_updates: true,
    marketing_emails: false
  });
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesError, setPreferencesError] = useState('');
  const [preferencesSuccess, setPreferencesSuccess] = useState('');

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState('');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 2FA states
  const [mfaFactors, setMfaFactors] = useState([]);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');
  const [currentFactor, setCurrentFactor] = useState(null);

  // Initialize form values when user data is available
  useEffect(() => {
    if (user?.user_metadata) {
      setFirstName(user.user_metadata.first_name || '');
      setLastName(user.user_metadata.last_name || '');
      setPhone(user.user_metadata.phone || '');
    }
  }, [user]);

  // Track API calls to prevent StrictMode duplicates
  const apiCallsInitiated = useRef(false);

  // Load email preferences, payment methods, and addresses on component mount
  useEffect(() => {
    if (!apiCallsInitiated.current) {
      apiCallsInitiated.current = true;
      loadEmailPreferences();
      loadPaymentMethods();
      loadAddresses();
      loadMfaFactors();
    }
  }, []);

  // Load MFA factors
  const loadMfaFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      setMfaFactors(data?.totp || []);
    } catch (error) {
      console.error('Error loading MFA factors:', error);
    }
  };

  // Enable MFA
  const handleEnableMfa = async () => {
    setMfaLoading(true);
    setMfaError('');
    setMfaSuccess('');

    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: user?.email || 'Account'
      });

      if (error) throw error;

      setCurrentFactor(data);
      setQrCodeUrl(data.totp.qr_code);
      setShowMfaModal(true);
    } catch (error) {
      console.error('Error enabling MFA:', error);
      setMfaError(error.message || 'Failed to enable 2FA');
    } finally {
      setMfaLoading(false);
    }
  };

  // Verify MFA setup
  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError('');

    try {
      if (!currentFactor) {
        throw new Error('No MFA factor available');
      }

      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: currentFactor.id,
        code: verificationCode
      });

      if (error) throw error;

      setMfaSuccess('Two-factor authentication enabled successfully!');
      setVerificationCode('');
      setCurrentFactor(null);
      setTimeout(() => {
        setShowMfaModal(false);
        setMfaSuccess('');
        loadMfaFactors();
      }, 2000);
    } catch (error) {
      console.error('Error verifying MFA:', error);
      setMfaError(error.message || 'Invalid verification code');
    } finally {
      setMfaLoading(false);
    }
  };

  // Disable MFA
  const handleDisableMfa = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    setMfaLoading(true);
    try {
      const factors = await supabase.auth.mfa.listFactors();
      if (factors.data?.totp?.length) {
        const factor = factors.data.totp[0];
        const { error } = await supabase.auth.mfa.unenroll({
          factorId: factor.id
        });
        if (error) throw error;

        await loadMfaFactors();
        setMfaSuccess('Two-factor authentication has been disabled');
        setTimeout(() => setMfaSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
      setMfaError(error.message || 'Failed to disable 2FA');
      setTimeout(() => setMfaError(''), 3000);
    } finally {
      setMfaLoading(false);
    }
  };

  // Close MFA modal and reset state
  const handleCloseMfaModal = () => {
    setShowMfaModal(false);
    setMfaError('');
    setMfaSuccess('');
    setVerificationCode('');
    setQrCodeUrl('');
    setCurrentFactor(null);
  };

  // Load email preferences from backend
  const loadEmailPreferences = async () => {
    try {
      const response = await apiService.getEmailPreferences();
      if (response.data && response.data.success) {
        setEmailPreferences(response.data.preferences);
      }
    } catch (error) {
      console.error('Error loading email preferences:', error);
    }
  };

  // Load payment methods
  const loadPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    setPaymentMethodsError('');

    try {
      const response = await apiService.getPaymentMethods();

      if (response.data && response.data.payment_methods) {
        setPaymentMethods(response.data.payment_methods);
      } else if (response.payment_methods) {
        setPaymentMethods(response.payment_methods);
      } else {
        setPaymentMethods([]);
      }
    } catch (error) {
      setPaymentMethodsError('Error loading payment methods');
      console.error('Error loading payment methods:', error);
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  // Load addresses
  const loadAddresses = async () => {
    setAddressesLoading(true);
    setAddressesError('');

    try {
      const response = await apiService.getUserAddresses();

      if (response.data && response.data.success) {
        setAddresses(response.data.addresses || []);
      } else {
        setAddresses([]);
      }
    } catch (error) {
      setAddressesError('Error loading addresses');
      console.error('Error loading addresses:', error);
    } finally {
      setAddressesLoading(false);
    }
  };

  const syncProfileWithBackend = async (profileData) => {
    try {
      const response = await api.post('/api/users/sync', {
        user_metadata: profileData
      });

      if (response.data && response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data?.error || 'Failed to sync profile with backend');
      }
    } catch (error) {
      console.error('Error syncing profile with backend:', error);
      throw error;
    }
  };

  // Handle email preferences update
  const handleUpdateEmailPreferences = async () => {
    setPreferencesLoading(true);
    setPreferencesError('');
    setPreferencesSuccess('');

    try {
      const response = await apiService.updateEmailPreferences(emailPreferences);
      if (response.data && response.data.success) {
        setPreferencesSuccess('Email preferences updated successfully!');
        setTimeout(() => setPreferencesSuccess(''), 3000);
      } else {
        setPreferencesError(response.data?.error || 'Failed to update email preferences');
      }
    } catch (error) {
      setPreferencesError('Error updating email preferences');
      console.error('Error updating email preferences:', error);
    } finally {
      setPreferencesLoading(false);
    }
  };

  // Handle preference change
  const handlePreferenceChange = (preference) => {
    setEmailPreferences(prev => ({
      ...prev,
      [preference]: !prev[preference]
    }));
  };

  // Payment method handlers
  const handleAddPaymentMethodSuccess = () => {
    setShowAddPaymentModal(false);
    loadPaymentMethods();
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    const paymentMethodToDelete = paymentMethods.find(pm => pm.id === paymentMethodId);

    if (paymentMethodToDelete && paymentMethodToDelete.is_default) {
      alert('Cannot delete the default payment method. Please set another payment method as default first.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      const response = await apiService.deletePaymentMethod(paymentMethodId);
      if (response.data && response.data.success) {
        loadPaymentMethods();
      } else {
        alert(response.data?.error || 'Failed to delete payment method');
      }
    } catch (error) {
      alert('Error deleting payment method');
      console.error('Error deleting payment method:', error);
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      const response = await apiService.setDefaultPaymentMethod({ payment_method_id: paymentMethodId });

      if (response.data && response.data.success) {
        loadPaymentMethods();
      } else {
        alert(response.data?.error || 'Failed to set default payment method');
      }
    } catch (error) {
      alert('Error setting default payment method');
      console.error('Error setting default payment method:', error);
    }
  };

  // Address handlers
  const handleAddAddress = (e) => {
    if (e && e.target) {
      e.target.blur();
    }
    setShowAddressModal(true);
  };

  const handleDeleteAddress = async (addressId) => {
    const addressToDelete = addresses.find(addr => addr.id === addressId);

    if (addressToDelete && addressToDelete.is_default) {
      alert('Cannot delete the default address. Please set another address as default first.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const response = await apiService.deleteUserAddress(addressId);
      if (response.data && response.data.success) {
        loadAddresses();
      } else {
        alert(response.data?.error || 'Failed to delete address');
      }
    } catch (error) {
      alert('Error deleting address');
      console.error('Error deleting address:', error);
    }
  };

  const handleSaveAddress = async (addressData) => {
    setAddressModalLoading(true);

    try {
      const response = await apiService.createUserAddress(addressData);

      if (response.data && response.data.success) {
        setShowAddressModal(false);
        loadAddresses();
      } else {
        throw new Error(response.data?.error || 'Failed to save address');
      }
    } catch (error) {
      console.error('Error saving address:', error);
    } finally {
      setAddressModalLoading(false);
    }
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const response = await apiService.setDefaultAddress(addressId);

      if (response.data && response.data.success) {
        loadAddresses();
      } else {
        alert(response.data?.error || 'Failed to set default address');
      }
    } catch (error) {
      alert('Error setting default address');
      console.error('Error setting default address:', error);
    }
  };

  // Password change handlers
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      setPasswordLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }

      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);

    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordError(error.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordError('');
    setPasswordSuccess('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');
    setFormSuccess('');

    try {
      const profileData = {
        first_name: firstName,
        last_name: lastName,
        phone: phone
      };

      const { error } = await supabase.auth.updateUser({
        data: profileData
      });

      if (error) throw error;

      await syncProfileWithBackend(profileData);

      setFormSuccess('Profile updated successfully!');
      setShowEditForm(false);

      setTimeout(() => setFormSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setFormError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-2">Loading account information...</p>
        </div>
      </div>
    );
  }

  return (
    <SuspensionCheck>
      <div className="max-w-7xl mx-auto px-4 pt-24 pb-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Account Settings</h2>
        </div>

        {formError && (
          <Alert variant="danger" className="mb-4">
            {formError}
          </Alert>
        )}

        {formSuccess && (
          <Alert variant="success" className="mb-4">
            {formSuccess}
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column - Profile, Addresses, and Security */}
          <div className="space-y-4">
            {/* Profile Information */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="bg-white py-3 px-6 border-b border-gray-200">
                <h5 className="text-lg font-semibold mb-0">Profile Information</h5>
              </div>
              <div className="p-6">
                <div className="mb-3">
                  <h6 className="text-gray-600 mb-2 text-sm font-medium">Email</h6>
                  <p className="mb-0">{user?.email}</p>
                </div>

                <div className="mb-3">
                  <h6 className="text-gray-600 mb-2 text-sm font-medium">Name</h6>
                  <p className="mb-0">
                    {user?.user_metadata?.first_name || ''} {user?.user_metadata?.last_name || ''}
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-gray-600 mb-2 text-sm font-medium">Phone</h6>
                  <p className="mb-0">{user?.user_metadata?.phone || 'Not provided'}</p>
                </div>

                <div className="mb-3">
                  <h6 className="text-gray-600 mb-2 text-sm font-medium">Account Created</h6>
                  <p className="mb-0">{new Date(user?.created_at).toLocaleDateString()}</p>
                </div>

                <div className="flex justify-between items-start">
                  <div>
                    <h6 className="text-gray-600 mb-2 text-sm font-medium">Last Sign In</h6>
                    <p className="mb-0">{new Date(user?.last_sign_in_at).toLocaleDateString()}</p>
                  </div>
                  <button
                    onClick={() => setShowEditForm(true)}
                    className="bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors"
                  >
                    Edit Profile
                  </button>
                </div>
              </div>
            </div>

            {/* Addresses Section */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="bg-white py-3 px-6 border-b border-gray-200 flex justify-between items-center">
                <h5 className="text-lg font-semibold mb-0">Addresses</h5>
                <button
                  onClick={handleAddAddress}
                  className="bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors flex items-center"
                >
                  <Plus size={16} className="mr-1" />
                  Add Address
                </button>
              </div>
              <div className="p-6">
                {addressesLoading ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" />
                    <span className="ml-2">Loading addresses...</span>
                  </div>
                ) : addressesError ? (
                  <Alert variant="danger">{addressesError}</Alert>
                ) : addresses.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <House size={48} className="mb-3 opacity-50 mx-auto" />
                    <p className="mb-0">No addresses added yet</p>
                    <p className="text-sm">Add an address to get started</p>
                  </div>
                ) : (
                  addresses.map((address) => (
                    <AddressCard
                      key={address.id}
                      address={address}
                      onDelete={handleDeleteAddress}
                      onSetDefault={handleSetDefaultAddress}
                      isDefault={address.is_default}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Password & Security */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="bg-white py-3 px-6 border-b border-gray-200">
                <h5 className="text-lg font-semibold mb-0">Password & Security</h5>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h6 className="mb-0 font-medium">Change Password</h6>
                    <button
                      onClick={() => setShowPasswordModal(true)}
                      className="bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h6 className="mb-0 font-medium">Two-Factor Authentication</h6>
                      {mfaFactors.length > 0 && (
                        <Badge variant="success" className="mt-1">Enabled</Badge>
                      )}
                    </div>
                    {mfaFactors.length > 0 ? (
                      <button
                        onClick={handleDisableMfa}
                        disabled={mfaLoading}
                        className="border border-red-500 text-red-600 px-4 py-2 rounded-full text-sm hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {mfaLoading ? <Spinner size="sm" /> : 'Disable'}
                      </button>
                    ) : (
                      <button
                        onClick={handleEnableMfa}
                        disabled={mfaLoading}
                        className="bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors disabled:opacity-50"
                      >
                        {mfaLoading ? <Spinner size="sm" /> : 'Enable'}
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 mb-0 text-sm">
                    {mfaFactors.length > 0
                      ? 'Your account is protected with two-factor authentication'
                      : 'Add an extra layer of security to your account'
                    }
                  </p>
                  {(mfaError || mfaSuccess) && (
                    <Alert variant={mfaError ? 'danger' : 'success'} className="mt-2 mb-0">
                      {mfaError || mfaSuccess}
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Payment Methods and Preferences */}
          <div className="space-y-4">
            {/* Payment Methods */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="bg-white py-3 px-6 border-b border-gray-200 flex justify-between items-center">
                <h5 className="text-lg font-semibold mb-0">Payment Methods</h5>
                <button
                  onClick={() => setShowAddPaymentModal(true)}
                  className="bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors flex items-center"
                >
                  <Plus className="mr-1" size={16} />
                  Add Payment Method
                </button>
              </div>
              <div className="p-6">
                {paymentMethodsLoading ? (
                  <div className="text-center py-4">
                    <Spinner size="md" />
                    <p className="mt-2 mb-0">Loading payment methods...</p>
                  </div>
                ) : paymentMethodsError ? (
                  <Alert variant="danger">
                    {paymentMethodsError}
                  </Alert>
                ) : paymentMethods.length === 0 ? (
                  <div className="text-center py-4">
                    <CreditCard size={48} className="text-gray-400 mb-3 mx-auto" />
                    <h6 className="text-gray-600">No payment methods</h6>
                    <p className="text-gray-600 mb-3">Add a payment method to manage your subscriptions</p>
                    <button
                      onClick={() => setShowAddPaymentModal(true)}
                      className="bg-black text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors"
                    >
                      Add Your First Payment Method
                    </button>
                  </div>
                ) : (
                  <div>
                    {paymentMethods.map((pm) => (
                      <PaymentMethodCard
                        key={pm.id}
                        paymentMethod={pm}
                        onDelete={handleDeletePaymentMethod}
                        onSetDefault={handleSetDefaultPaymentMethod}
                        isDefault={pm.is_default}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="bg-white py-3 px-6 border-b border-gray-200">
                <h5 className="text-lg font-semibold mb-0">Notification Preferences</h5>
              </div>
              <div className="p-6">
                {preferencesError && (
                  <Alert variant="danger" className="mb-3">
                    {preferencesError}
                  </Alert>
                )}
                {preferencesSuccess && (
                  <Alert variant="success" className="mb-3">
                    {preferencesSuccess}
                  </Alert>
                )}

                <div className="mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-black rounded focus:ring-black border-gray-300"
                      checked={emailPreferences.email_notifications}
                      onChange={() => handlePreferenceChange('email_notifications')}
                    />
                    <span className="ml-3 text-gray-700">Email Notifications</span>
                  </label>
                  <p className="text-gray-600 text-sm ml-8 mb-0">Receive emails about account activity and updates</p>
                </div>

                <div className="mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-black rounded focus:ring-black border-gray-300"
                      checked={emailPreferences.price_alerts}
                      onChange={() => handlePreferenceChange('price_alerts')}
                    />
                    <span className="ml-3 text-gray-700">Price Alerts</span>
                  </label>
                  <p className="text-gray-600 text-sm ml-8 mb-0">Get notified when metal prices change significantly</p>
                </div>

                <div className="mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-black rounded focus:ring-black border-gray-300"
                      checked={emailPreferences.delivery_updates}
                      onChange={() => handlePreferenceChange('delivery_updates')}
                    />
                    <span className="ml-3 text-gray-700">Delivery Updates</span>
                  </label>
                  <p className="text-gray-600 text-sm ml-8 mb-0">Receive notifications about your deliveries</p>
                </div>

                <div className="mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-black rounded focus:ring-black border-gray-300"
                      checked={emailPreferences.marketing_emails}
                      onChange={() => handlePreferenceChange('marketing_emails')}
                    />
                    <span className="ml-3 text-gray-700">Marketing Emails</span>
                  </label>
                  <p className="text-gray-600 text-sm ml-8 mb-0">Receive promotional offers and newsletters</p>
                </div>

                <button
                  onClick={handleUpdateEmailPreferences}
                  disabled={preferencesLoading}
                  className="mt-3 bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {preferencesLoading ? (
                    <>
                      <Spinner size="sm" className="inline mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Preferences'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile Modal */}
        <Modal show={showEditForm} onHide={() => setShowEditForm(false)} size="lg" title="Edit Profile">
          <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter last name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-black text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="inline mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Add Payment Method Modal */}
        <Modal show={showAddPaymentModal} onHide={() => setShowAddPaymentModal(false)} title="Add Payment Method">
          <div className="p-6">
            <Elements stripe={stripePromise}>
              <AddPaymentMethodForm
                onSuccess={handleAddPaymentMethodSuccess}
                onCancel={() => setShowAddPaymentModal(false)}
              />
            </Elements>
          </div>
        </Modal>

        {/* Address Modal */}
        <AddressModal
          show={showAddressModal}
          onHide={handleCloseAddressModal}
          onSave={handleSaveAddress}
          address={null}
          loading={addressModalLoading}
        />

        {/* Password Change Modal */}
        <Modal show={showPasswordModal} onHide={handleClosePasswordModal} title="Change Password">
          <div className="p-6">
            {passwordError && (
              <Alert variant="danger" className="mb-3">
                {passwordError}
              </Alert>
            )}
            {passwordSuccess && (
              <Alert variant="success" className="mb-3">
                {passwordSuccess}
              </Alert>
            )}

            <form onSubmit={handlePasswordChange}>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
                <p className="text-gray-600 text-sm mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="bg-black text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {passwordLoading ? (
                    <>
                      <Spinner size="sm" className="inline mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClosePasswordModal}
                  className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* MFA Setup Modal */}
        <Modal show={showMfaModal} onHide={handleCloseMfaModal} title="Enable Two-Factor Authentication">
          <div className="p-6">
            {mfaError && (
              <Alert variant="danger" className="mb-3">
                {mfaError}
              </Alert>
            )}
            {mfaSuccess && (
              <Alert variant="success" className="mb-3">
                {mfaSuccess}
              </Alert>
            )}

            <div className="mb-4">
              <h6 className="font-semibold">Step 1: Scan QR Code</h6>
              <p className="text-gray-600 text-sm">
                Use an authenticator app like Google Authenticator, Authy, or 1Password to scan this QR code:
              </p>
              {qrCodeUrl && (
                <div className="text-center mb-3">
                  <img src={qrCodeUrl} alt="QR Code for 2FA setup" className="max-w-[200px] mx-auto" />
                </div>
              )}
            </div>

            <form onSubmit={handleVerifyMfa}>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Step 2: Enter Verification Code</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  required
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  name="verification-code"
                  inputMode="numeric"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
                <p className="text-gray-600 text-sm mt-1">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={mfaLoading || verificationCode.length !== 6}
                  className="bg-black text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {mfaLoading ? (
                    <>
                      <Spinner size="sm" className="inline mr-2" />
                      Verifying...
                    </>
                  ) : (
                    'Enable 2FA'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseMfaModal}
                  className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </SuspensionCheck>
  );
};

export default Account;
